/**
 * Task #2315 — client-side fallback for the cancellation policy summary.
 *
 * When GET /bookings/:publicId/cancellation-policy fails (network, 5xx,
 * or the booking 09F106DF "no PI / no capture" shape) we still need to
 * render the cancel modal AND keep the Continue button reachable, so we
 * synthesize a CancellationPolicySummary from the trip's frozen
 * `rate_plan` / `refund_policy_snapshot`. This module is the pure
 * derivation logic, kept outside TripDetailPage so it can be unit-tested
 * without rendering the (~3000-line) page.
 *
 * The cancel POST always re-evaluates policy on the server, so the
 * fallback only affects the renter-facing preview text — never the
 * actual refund math.
 */

import type { CancellationPolicySummary } from "./bookings-api";

export interface FallbackTripShape {
  isOwner?: boolean;
  rate_plan?: "flexible" | "non_refundable" | null;
  refund_policy_snapshot?: { rate_plan: "flexible" | "non_refundable" } | null;
  started_at?: string | null;
}

export function buildFallbackPolicySummary(
  trip: FallbackTripShape,
  currency: string,
  nowMs: number = Date.now(),
): CancellationPolicySummary {
  const ratePlan = trip.rate_plan
    ?? trip.refund_policy_snapshot?.rate_plan
    ?? "flexible";
  const isFlexible = ratePlan === "flexible";
  const cancelledByOwner = !!trip.isOwner;
  const startedMs = trip.started_at ? Date.parse(trip.started_at) : NaN;
  const haveStart = Number.isFinite(startedMs);
  const cutoffAt = isFlexible && haveStart
    ? new Date(startedMs - 48 * 3600 * 1000).toISOString()
    : null;
  const within48h = isFlexible && haveStart && (startedMs - nowMs) <= 48 * 3600 * 1000;
  const policyLabel = cancelledByOwner
    ? "Host cancellation"
    : isFlexible ? "Flexible, refundable cancellation" : "Non-refundable";
  const summary = cancelledByOwner
    ? "Full refund: host cancellation."
    : isFlexible
      ? (within48h
          ? "Flexible plan: within 48 hours of pick-up. No refund will be issued."
          : "Flexible plan: full refund if you cancel before the 48-hour cutoff.")
      : "Non-refundable plan: no refund will be issued.";
  const reason = cancelledByOwner
    ? "owner_cancellation_full_refund"
    : isFlexible
      ? (within48h ? "flexible_within_48h_no_refund" : "flexible_more_than_48h_full_refund")
      : "non_refundable_no_refund";
  return {
    policy: isFlexible ? "flexible" : "non-refundable",
    policyLabel,
    summary,
    rules: [],
    cutoffAt,
    pickUp: trip.started_at ?? null,
    currency,
    netCaptured: 0,
    actor: cancelledByOwner ? "owner" : "renter",
    chargeStatus: "none",
    policySource: "missing",
    refundPreview: {
      amount: 0,
      reason,
      policy: policyLabel,
      breakdown: null,
    },
  };
}
